import React from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, FileText, Wallet, Briefcase, LineChart, Banknote, Clock4, BadgeIndianRupee } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { LenderCard } from "../components/LenderCard";
import { LeadForm } from "../components/LeadForm";
import { EMICalculator } from "../components/EMICalculator";
import { FAQSection } from "../components/FAQSection";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PERSONAL_LENDERS, BUSINESS_LENDERS, OVERDRAFT_LENDERS } from "../data/lenders";

const CONFIG = {
  personal: {
    title: "Personal Loan",
    tagline: "Instant Personal Loans up to ₹50 Lakh",
    icon: Wallet,
    desc: "Quick, unsecured personal loans for weddings, medical emergencies, travel, education or any planned expense. Compare 16+ lenders in one place.",
    rate: "9.99%", maxAmount: "₹50 Lakh", tenure: "Up to 84 months",
    benefits: ["No collateral required", "Disbursal in 24-72 hours", "Flexible tenure 12-84 months", "Minimal documentation", "Pre-approved offers", "Online application"],
    features: ["Loans from ₹50,000 to ₹50 Lakh", "Interest from 9.99% onwards", "Repay anytime — no penalty after 6 months", "Soft enquiry doesn't impact CIBIL", "Dedicated relationship manager"],
    eligibility: ["Indian citizen aged 21-60", "Salaried: Monthly income ₹25,000+", "Self-employed: ITR with ₹3L+ annual income", "Minimum 1 year employment / 2 years business vintage", "CIBIL score 700+ recommended"],
    docs: ["PAN card & Aadhaar", "Last 3 months salary slips / ITR (2 years)", "Last 3 months bank statement", "Address proof", "1 passport-size photograph"],
    lenders: PERSONAL_LENDERS,
    basePath: "/personal-loan",
    product: "personal",
    procFee: "Up to 3.5%",
  },
  business: {
    title: "Business Loan",
    tagline: "Business Loans up to ₹2 Crore",
    icon: Briefcase,
    desc: "Working capital, expansion, inventory, equipment — get collateral-free business loans designed for MSMEs, startups, and growing enterprises.",
    rate: "11.90%", maxAmount: "₹2 Crore", tenure: "Up to 60 months",
    benefits: ["Collateral-free up to ₹2 Cr", "Quick approval in 5-10 days", "Flexible repayment", "Tax-deductible interest", "No prepayment after 6 months", "GST-data based offers"],
    features: ["Loans from ₹1 Lakh to ₹2 Crore", "Interest from 11.90% onwards", "Tenure 12 to 60 months", "Use for any business purpose", "Top-up available"],
    eligibility: ["Indian-registered business (Proprietor/Partnership/Pvt Ltd/LLP)", "Business vintage: 2+ years", "Annual turnover ₹40 Lakh+", "Owner age 25-65", "Owner CIBIL 700+"],
    docs: ["KYC: PAN & Aadhaar of owner(s)", "Business proof: GST cert, Udyam, Shop Act", "ITR last 2 years", "GST returns last 12 months", "Bank statements last 12 months", "Audited financials (if applicable)"],
    lenders: BUSINESS_LENDERS,
    basePath: "/business-loan",
    product: "business",
    procFee: "Up to 2.5%",
  },
  overdraft: {
    title: "Flexi Overdraft Loan",
    tagline: "Flexi Overdraft up to ₹2 Crore — Pay interest only on usage",
    icon: LineChart,
    desc: "A revolving credit limit that you can withdraw and repay multiple times. Interest is charged only on the utilised amount, saving you up to 60% compared to a regular term loan.",
    rate: "12.50%", maxAmount: "₹2 Crore", tenure: "Up to 60 months",
    benefits: ["Interest only on utilised amount", "Withdraw & repay multiple times", "Limits up to ₹2 Crore", "Flexible tenure", "No part-payment charges", "Convert to EMI anytime"],
    features: ["Credit limit ₹1 Lakh to ₹2 Crore", "Interest from 12.50% on usage", "Auto-renewal annually", "Repay flexibly", "Top-up & enhancement available"],
    eligibility: ["Salaried (₹40,000+) or Self-Employed / Business", "Business vintage 2+ years (for SE/Business)", "Age 23-65", "CIBIL 720+"],
    docs: ["PAN & Aadhaar", "Salary slips / ITR last 2 years", "Bank statements 6-12 months", "Business proof (if applicable)"],
    lenders: OVERDRAFT_LENDERS,
    basePath: "/overdraft-loan",
    product: "overdraft",
    procFee: "Up to 2.5%",
  },
};

export default function LoanProductPage({ type }) {
  const cfg = CONFIG[type];
  const Icon = cfg.icon;

  return (
    <div data-testid={`loan-page-${type}`}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EAF5FD] via-white to-white ol-grain">
        <div className="ol-container py-10 lg:py-16">
          <Breadcrumbs items={[{ label: cfg.title }]}/>
          <div className="grid lg:grid-cols-12 gap-10 items-start mt-6">
            <div className="lg:col-span-7">
              <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0 mb-4">
                <Icon size={12} className="mr-1.5"/> {cfg.title}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-[#273B59] tracking-tight leading-[1.05]">{cfg.tagline}</h1>
              <p className="mt-5 text-lg text-[#475569] max-w-xl">{cfg.desc}</p>
              <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl">
                <Highlight label="Starting Rate" value={`${cfg.rate} p.a.`}/>
                <Highlight label="Max Amount" value={cfg.maxAmount}/>
                <Highlight label="Tenure" value={cfg.tenure}/>
              </div>
            </div>
            <div className="lg:col-span-5">
              <LeadForm defaultType={type} title={`Apply for ${cfg.title}`} source={`${type}-hero`}/>
            </div>
          </div>
        </div>
      </section>

      {/* Lender comparison */}
      <section data-testid="lenders-section" className="ol-section">
        <div className="ol-container">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">Lenders</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#273B59] font-heading">Compare {cfg.title} Offers from Leading Banks & NBFCs</h2>
            <p className="mt-3 text-[#475569]">All rates and terms are indicative. Final approval and offers are at the lender's discretion.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cfg.lenders.map((slug, i) => (
              <LenderCard key={slug} slug={slug} product={cfg.product} basePath={cfg.basePath} index={i}/>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Features */}
      <section className="bg-[#F7F9FC] ol-section">
        <div className="ol-container grid md:grid-cols-2 gap-10">
          <Block title="Key Benefits" items={cfg.benefits} icon={CheckCircle2}/>
          <Block title="Features" items={cfg.features} icon={CheckCircle2}/>
        </div>
      </section>

      {/* Eligibility + Docs */}
      <section className="ol-section">
        <div className="ol-container grid md:grid-cols-2 gap-10">
          <Block title="Eligibility Criteria" items={cfg.eligibility} icon={BadgeIndianRupee}/>
          <Block title="Documents Required" items={cfg.docs} icon={FileText}/>
        </div>
      </section>

      {/* EMI Calculator */}
      <section className="bg-gradient-to-br from-[#EAF5FD] to-white ol-section">
        <div className="ol-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">Plan ahead</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#273B59] font-heading">{cfg.title} EMI Calculator</h2>
          </div>
          <EMICalculator productLabel={cfg.title} initialPrincipal={type === "business" ? 1500000 : 500000}/>
        </div>
      </section>

      <FAQSection/>

      {/* CTA */}
      <section className="bg-[#273B59] text-white py-14">
        <div className="ol-container flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading">Ready to apply for your {cfg.title}?</h2>
            <p className="mt-1 text-white/80">Get matched with the best lender in minutes.</p>
          </div>
          <Link to={`/apply?type=${type}`}><Button data-testid="bottom-cta-btn" size="lg" className="ol-btn-primary h-12 px-8">Apply Now</Button></Link>
        </div>
      </section>
    </div>
  );
}

const Highlight = ({ label, value }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 text-center">
    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
    <div className="mt-1 text-sm font-bold text-[#1088D4]">{value}</div>
  </div>
);

const Block = ({ title, items, icon: Icon }) => (
  <div>
    <h2 className="text-2xl sm:text-3xl font-bold text-[#273B59] font-heading">{title}</h2>
    <ul className="mt-5 space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-[#273B59]">
          <Icon size={18} className="text-[#16A34A] mt-0.5 shrink-0"/>
          <span className="text-sm leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  </div>
);
